import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  ReferredUserSummary,
  UserPromotion,
  UserPromotionStatus,
  UserPromotionsKpis,
  UserReferralsKpis,
} from "../../components/Users/UserManager/Promotions/userPromotionsTypes";
import { getPrivateInstance } from "../_privateInstance";

export type UserPromotionsDateRange = {
  userId: string;
  start_date: string;
  end_date: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const coerceNumber = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const coerceNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const coerceString = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
};

const coerceNullableString = (value: unknown): string | null => {
  const valueAsString = coerceString(value);
  return valueAsString === "" ? null : valueAsString;
};

const pickValue = (
  record: Record<string, unknown>,
  keys: readonly string[],
): unknown => {
  for (const key of keys) {
    if (key in record && record[key] !== undefined) return record[key];
  }
  return undefined;
};

const splitList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap(splitList);
  }
  if (typeof value !== "string") return [];
  return value
    .split(/[/,]/)
    .map((part) => part.trim())
    .filter(Boolean);
};

const extractPayloadRecord = (body: unknown): Record<string, unknown> => {
  if (!isRecord(body)) return {};
  return isRecord(body.data) ? body.data : body;
};

const extractPayloadList = (body: unknown): unknown[] => {
  if (!isRecord(body)) return [];
  const data = body.data;
  if (Array.isArray(data)) return data;
  if (!isRecord(data)) return [];
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.promotions)) return data.promotions;
  if (Array.isArray(data.referrals)) return data.referrals;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

const buildPath = (route: string, userId: string): string =>
  route.replace("{user_id}", encodeURIComponent(userId));

const dateParams = (params: UserPromotionsDateRange) => ({
  start_date: params.start_date,
  end_date: params.end_date,
});

const parsePromotionStatus = (
  rawStatus: unknown,
  claimedAt: string | null,
): UserPromotionStatus => {
  const status = coerceString(rawStatus).toLowerCase();
  if (status === "claimed" || status === "unclaimed") return status;
  return claimedAt ? "claimed" : "unclaimed";
};

const parseUserStatus = (rawStatus: unknown): ReferredUserSummary["status"] => {
  const status = coerceString(rawStatus).toLowerCase();
  if (status === "inactive" || status === "suspended" || status === "deleted") {
    return "inactive";
  }
  return "active";
};

const parseKyc = (rawKyc: unknown): string => {
  const kyc = coerceString(rawKyc);
  if (kyc === "failed") return "rejected";
  return kyc;
};

export const parseUserPromotionsKpisResponse = (
  body: unknown,
): UserPromotionsKpis => {
  const payload = extractPayloadRecord(body);
  return {
    received: coerceNumber(pickValue(payload, ["received"])),
    claimed: coerceNumber(pickValue(payload, ["claimed"])),
    sportsbookFreeCashClaimed: coerceNumber(
      pickValue(payload, [
        "sportsbook_free_cash_claimed",
        "sportsbookFreeCashClaimed",
      ]),
    ),
    casinoCashClaimed: coerceNumber(
      pickValue(payload, ["casino_cash_claimed", "casinoCashClaimed"]),
    ),
  };
};

export const parseUserReferralsKpisResponse = (
  body: unknown,
): UserReferralsKpis => {
  const payload = extractPayloadRecord(body);
  return {
    totalReferred: coerceNumber(
      pickValue(payload, ["total_referred", "totalReferred"]),
    ),
    purchasingUsers: coerceNumber(
      pickValue(payload, ["purchasing_users", "purchasingUsers"]),
    ),
    lifetimeValue: coerceNumber(
      pickValue(payload, ["lifetime_value", "lifetimeValue"]),
    ),
  };
};

const parsePromotionRow = (
  raw: unknown,
  index: number,
): UserPromotion | null => {
  if (!isRecord(raw)) return null;

  const promoId = coerceString(pickValue(raw, ["promo_id", "promoId"]));
  const sentAt = coerceString(
    pickValue(raw, ["sent_at", "sentAt", "date_time"]),
  );
  const claimedAt = coerceNullableString(
    pickValue(raw, ["claimed_at", "claimedAt"]),
  );

  return {
    id: coerceString(pickValue(raw, ["id"])) || `${promoId || "promo"}-${sentAt || index}`,
    sentAt,
    promoId,
    promoName: coerceString(pickValue(raw, ["promo_name", "promoName", "name"])),
    description: coerceString(pickValue(raw, ["description"])),
    promoTypes: splitList(pickValue(raw, ["promo_types", "promoTypes", "types"])),
    potentialBonuses: splitList(
      pickValue(raw, ["potential_bonuses", "potentialBonuses"]),
    ),
    status: parsePromotionStatus(pickValue(raw, ["status"]), claimedAt),
    claimedAt,
    claimedAmount: coerceNullableNumber(
      pickValue(raw, ["claimed_amount", "claimedAmount", "amount"]),
    ),
    moneyType: coerceNullableString(
      pickValue(raw, ["money_type", "moneyType"]),
    ),
  };
};

const parseReferralRow = (
  raw: unknown,
  index: number,
): ReferredUserSummary | null => {
  if (!isRecord(raw)) return null;

  const userId = coerceString(pickValue(raw, ["user_id", "id"]));
  const invitationDate = coerceNullableString(
    pickValue(raw, ["invitation_date", "invitationDate", "date_joined"]),
  );

  return {
    id: userId || `referral-${index}`,
    ipAddress: coerceString(
      pickValue(raw, ["ip_address", "ipAddress", "last_ip"]),
    ),
    referralCode: coerceString(
      pickValue(raw, ["referral_code", "referralCode"]),
    ),
    username: coerceString(pickValue(raw, ["username"])),
    email: coerceString(pickValue(raw, ["email"])),
    deposit: coerceNullableNumber(
      pickValue(raw, ["deposit", "initial_deposit", "first_deposit_amount"]),
    ),
    depositDate: coerceNullableString(
      pickValue(raw, [
        "deposit_date",
        "depositDate",
        "initial_deposit_ts",
        "first_purchase_at",
      ]),
    ),
    totalPurchases: coerceNumber(
      pickValue(raw, ["total_purchases", "totalPurchases"]),
    ),
    totalBets: coerceNumber(pickValue(raw, ["total_bets", "totalBets"])),
    totalPrizeRedemptions: coerceNumber(
      pickValue(raw, ["total_prize_redemptions", "totalPrizeRedemptions"]),
    ),
    kyc: parseKyc(pickValue(raw, ["kyc", "kyc_status"])),
    status: parseUserStatus(pickValue(raw, ["status"])),
    registrationScore: coerceNumber(
      pickValue(raw, ["registration_score", "registrationScore"]),
    ),
    invitationDate,
  };
};

export const parseUserPromotionsResponse = (body: unknown): UserPromotion[] =>
  extractPayloadList(body)
    .map(parsePromotionRow)
    .filter((row): row is UserPromotion => row !== null);

export const parseUserReferralsResponse = (
  body: unknown,
): ReferredUserSummary[] =>
  extractPayloadList(body)
    .map(parseReferralRow)
    .filter((row): row is ReferredUserSummary => row !== null);

export const fetchUserPromotionsKpis = async (
  params: UserPromotionsDateRange,
): Promise<UserPromotionsKpis> => {
  const instance = getPrivateInstance();
  const response = await instance.get(
    buildPath(API_ROUTES.GET_USER_PROMOTIONS_KPIS, params.userId),
    { params: dateParams(params) },
  );
  return parseUserPromotionsKpisResponse(response?.data);
};

export const fetchUserPromotions = async (
  params: UserPromotionsDateRange,
): Promise<UserPromotion[]> => {
  const instance = getPrivateInstance();
  const response = await instance.get(
    buildPath(API_ROUTES.GET_USER_PROMOTIONS, params.userId),
    { params: dateParams(params) },
  );
  return parseUserPromotionsResponse(response?.data);
};

export const fetchUserReferralsKpis = async (
  params: UserPromotionsDateRange,
): Promise<UserReferralsKpis> => {
  const instance = getPrivateInstance();
  const response = await instance.get(
    buildPath(API_ROUTES.GET_USER_REFERRALS_KPIS, params.userId),
    { params: dateParams(params) },
  );
  return parseUserReferralsKpisResponse(response?.data);
};

export const fetchUserReferrals = async (
  params: UserPromotionsDateRange,
): Promise<ReferredUserSummary[]> => {
  const instance = getPrivateInstance();
  const response = await instance.get(
    buildPath(API_ROUTES.GET_USER_REFERRALS, params.userId),
    { params: dateParams(params) },
  );
  return parseUserReferralsResponse(response?.data);
};

const useUserPromotionsQuery = <T>(
  queryKey: string,
  params: UserPromotionsDateRange | null,
  queryFn: (params: UserPromotionsDateRange) => Promise<T>,
  enabled: boolean,
): UseQueryResult<T, Error> =>
  useQuery<T, Error>({
    queryKey: [
      queryKey,
      params?.userId ?? "",
      params?.start_date ?? "",
      params?.end_date ?? "",
    ],
    queryFn: () => queryFn(params as UserPromotionsDateRange),
    enabled: Boolean(
      enabled && params?.userId && params?.start_date && params?.end_date,
    ),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });

export const useGetUserPromotionsKpis = (
  params: UserPromotionsDateRange | null,
  enabled = true,
): UseQueryResult<UserPromotionsKpis, Error> =>
  useUserPromotionsQuery(
    "GET_USER_PROMOTIONS_KPIS",
    params,
    fetchUserPromotionsKpis,
    enabled,
  );

export const useGetUserPromotions = (
  params: UserPromotionsDateRange | null,
  enabled = true,
): UseQueryResult<UserPromotion[], Error> =>
  useUserPromotionsQuery(
    "GET_USER_PROMOTIONS",
    params,
    fetchUserPromotions,
    enabled,
  );

export const useGetUserReferralsKpis = (
  params: UserPromotionsDateRange | null,
  enabled = true,
): UseQueryResult<UserReferralsKpis, Error> =>
  useUserPromotionsQuery(
    "GET_USER_REFERRALS_KPIS",
    params,
    fetchUserReferralsKpis,
    enabled,
  );

export const useGetUserReferrals = (
  params: UserPromotionsDateRange | null,
  enabled = true,
): UseQueryResult<ReferredUserSummary[], Error> =>
  useUserPromotionsQuery(
    "GET_USER_REFERRALS",
    params,
    fetchUserReferrals,
    enabled,
  );
